import { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const location = useLocation();

  const handleScroll = useCallback(() => {
    setScrolled(window.scrollY > 60);
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  useEffect(() => {
    setMobileOpen(false);
    setSearchOpen(false);
  }, [location.pathname]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      setSearchOpen(false);
      setSearchQuery('');
    }
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? 'bg-background-50/95 backdrop-blur-md shadow-[0_1px_0_oklch(var(--background-200))]'
          : 'bg-transparent'
      }`}
    >
      <nav className="w-full px-4 md:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-18">
          <Link
            to="/"
            className="flex items-center gap-2 whitespace-nowrap cursor-pointer group"
          >
            <div className="w-9 h-9 md:w-10 md:h-10 flex items-center justify-center rounded-lg bg-primary-500">
              <i className="ri-movie-2-fill text-lg md:text-xl text-background-50 dark:text-foreground-950"></i>
            </div>
            <span className="text-xl md:text-2xl font-heading font-bold text-foreground-50 tracking-wide group-hover:text-primary-400 transition-colors duration-300">
              星辰影院
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-1">
            <Link
              to="/"
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 whitespace-nowrap cursor-pointer ${
                location.pathname === '/'
                  ? 'text-primary-400 bg-primary-500/10'
                  : 'text-foreground-300 hover:text-foreground-50 hover:bg-background-100'
              }`}
            >
              首页
            </Link>
            {categories.slice(0, 6).map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 whitespace-nowrap cursor-pointer ${
                  location.pathname === `/category/${cat.slug}`
                    ? 'text-primary-400 bg-primary-500/10'
                    : 'text-foreground-300 hover:text-foreground-50 hover:bg-background-100'
                }`}
              >
                {cat.name}
              </Link>
            ))}
          </div>

          <div className="hidden md:flex items-center gap-3">
            <button
              onClick={() => setSearchOpen(!searchOpen)}
              className="w-9 h-9 flex items-center justify-center rounded-lg text-foreground-400 hover:text-foreground-50 hover:bg-background-100 transition-all duration-300 cursor-pointer whitespace-nowrap"
              aria-label="搜索"
            >
              <i className="ri-search-line text-lg"></i>
            </button>
            <Link
              to="/about"
              className="px-4 py-2 rounded-lg text-sm font-medium text-foreground-300 hover:text-foreground-50 hover:bg-background-100 transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              关于我们
            </Link>
          </div>

          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="md:hidden w-9 h-9 flex items-center justify-center rounded-lg text-foreground-300 hover:text-foreground-50 transition-colors cursor-pointer whitespace-nowrap"
            aria-label="菜单"
          >
            <i className={`text-xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>

        {searchOpen && (
          <div className="hidden md:block pb-4 animate-fade-in">
            <form onSubmit={handleSearch} className="flex items-center gap-2">
              <div className="relative flex-1">
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-500 text-sm"></i>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="搜索电视剧、演员、类型…"
                  className="w-full pl-10 pr-4 py-2.5 rounded-lg bg-background-100 border border-background-300/60 text-foreground-100 text-sm placeholder-foreground-500 focus:outline-none focus:border-primary-500/50 focus:ring-1 focus:ring-primary-500/30 transition-all duration-300"
                />
              </div>
              <button
                type="submit"
                className="px-5 py-2.5 rounded-lg bg-primary-500 text-background-50 dark:text-foreground-950 text-sm font-medium hover:bg-primary-600 transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                搜索
              </button>
            </form>
          </div>
        )}
      </nav>

      {mobileOpen && (
        <div className="md:hidden bg-background-50/98 backdrop-blur-md border-t border-background-200/60 animate-fade-in">
          <div className="px-4 py-4 space-y-1">
            <Link
              to="/"
              className="block px-4 py-3 rounded-lg text-base font-medium text-foreground-200 hover:text-foreground-50 hover:bg-background-100 transition-colors cursor-pointer"
            >
              首页
            </Link>
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className="block px-4 py-3 rounded-lg text-base font-medium text-foreground-300 hover:text-foreground-50 hover:bg-background-100 transition-colors cursor-pointer"
              >
                {cat.name}
              </Link>
            ))}
            <Link
              to="/about"
              className="block px-4 py-3 rounded-lg text-base font-medium text-foreground-300 hover:text-foreground-50 hover:bg-background-100 transition-colors cursor-pointer"
            >
              关于我们
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}